/**
 * useStatisticsData
 *
 * 통계 데이터 집계 hook
 */

import { useMemo } from 'react'
import { useTransactions } from './useTransactions'
import { useCategories } from '../../category/hooks/useCategories'
import type { Period, StatisticsData } from '../types/Statistics'
import {
  aggregateByCategory,
  createTimeSeriesData,
  calculateComparison,
  createPeriod
} from '../utils/statisticsCalculations'

export function useStatisticsData(period: Period) {
  // 현재 기간 트랜잭션
  const { transactions, loading, error } = useTransactions({
    startDate: period.startDate,
    endDate: period.endDate
  })

  // 전기 트랜잭션 (비교용)
  const previousPeriod = useMemo(() => {
    const refDate = new Date(period.startDate)

    switch (period.type) {
      case 'week':
        refDate.setDate(refDate.getDate() - 7)
        break
      case 'month':
        refDate.setMonth(refDate.getMonth() - 1)
        break
      case 'year':
        refDate.setFullYear(refDate.getFullYear() - 1)
        break
    }

    return createPeriod(period.type, refDate)
  }, [period])

  const { transactions: previousTransactions } = useTransactions({
    startDate: previousPeriod.startDate,
    endDate: previousPeriod.endDate
  })

  // 카테고리 데이터
  const { expenseCategories } = useCategories()

  // 통계 데이터 계산
  const statisticsData: StatisticsData | null = useMemo(() => {
    if (!transactions || !expenseCategories) return null

    // 요약 계산
    const totalIncome = transactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0)

    const totalExpense = transactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0)

    const comparison = calculateComparison(transactions, previousTransactions || [])

    // 카테고리별 집계
    const categoryStats = aggregateByCategory(transactions, expenseCategories)

    // 시계열 데이터
    const timeSeriesData = createTimeSeriesData(transactions, period)

    return {
      period,
      summary: {
        totalIncome,
        totalExpense,
        netAmount: totalIncome - totalExpense,
        transactionCount: transactions.length,
        ...comparison
      },
      categoryStats,
      timeSeriesData,
      topCategories: categoryStats.slice(0, 5)
    }
  }, [transactions, previousTransactions, expenseCategories, period])

  return {
    data: statisticsData,
    loading,
    error
  }
}
